import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert,
} from 'react-bootstrap';
import DiscountCodeService from '../../../Services/DiscountCodeService';

function DiscountCodeInputModal({
  show, onHide, discountCode, onSuccess,
}) {
  const [formData, setFormData] = useState({
    code: '',
    discount_percentage: '',
    description: '',
    valid_from: '',
    valid_until: '',
    max_uses: '',
    active: 1,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (discountCode) {
      setFormData({
        Id: discountCode.Id,
        code: discountCode.code || '',
        discount_percentage: discountCode.discount_percentage || '',
        description: discountCode.description || '',
        valid_from: discountCode.valid_from ? discountCode.valid_from.slice(0, 10) : '',
        valid_until: discountCode.valid_until ? discountCode.valid_until.slice(0, 10) : '',
        max_uses: discountCode.max_uses || '',
        active: discountCode.active ?? 1,
      });
    } else {
      setFormData({
        code: '',
        discount_percentage: '',
        description: '',
        valid_from: '',
        valid_until: '',
        max_uses: '',
        active: 1,
      });
    }
    setError('');
  }, [discountCode, show]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => {
      let newValue = value;
      if (type === 'number') {
        newValue = value === '' ? '' : Number(value);
      }
      return { ...prev, [name]: newValue };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // valid_from / valid_until come from <input type="date"> as bare
      // YYYY-MM-DD strings. new Date(...) parses these as midnight
      // UTC, which means a code stamped "valid until 2026-05-31" used
      // to expire at midnight UTC on May 31 — i.e. ~01:00 Budapest on
      // the supposed-final-day. Snap start-of-day for valid_from and
      // end-of-day for valid_until so the validity window covers the
      // whole calendar day the admin chose.
      const dataToSubmit = {
        ...formData,
        discount_percentage: Number(formData.discount_percentage),
        max_uses: formData.max_uses ? Number(formData.max_uses) : 0,
        valid_from: formData.valid_from
          ? new Date(`${formData.valid_from}T00:00:00.000Z`).toISOString()
          : undefined,
        valid_until: formData.valid_until
          ? new Date(`${formData.valid_until}T23:59:59.999Z`).toISOString()
          : undefined,
      };

      if (formData.Id) {
        await DiscountCodeService.updateDiscountCode(dataToSubmit);
      } else {
        await DiscountCodeService.createDiscountCode(dataToSubmit);
      }

      onSuccess();
      onHide();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save discount code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {formData.Id ? 'Edit Discount Code' : 'Create Discount Code'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Code *</Form.Label>
                <Form.Control
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="e.g., SUMMER20"
                  required
                  style={{ textTransform: 'uppercase' }}
                />
                <Form.Text className="text-muted">
                  Will be converted to uppercase automatically
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Discount Percentage *</Form.Label>
                <Form.Control
                  type="number"
                  name="discount_percentage"
                  value={formData.discount_percentage}
                  onChange={handleChange}
                  placeholder="e.g., 10"
                  min="0"
                  max="100"
                  required
                />
                <Form.Text className="text-muted">
                  Enter a value between 0 and 100
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optional description for admin reference"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Valid From</Form.Label>
                <Form.Control
                  type="date"
                  name="valid_from"
                  value={formData.valid_from}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">
                  Leave empty for no start date restriction
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Valid Until</Form.Label>
                <Form.Control
                  type="date"
                  name="valid_until"
                  value={formData.valid_until}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">
                  Leave empty for no end date restriction
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Maximum Uses</Form.Label>
                <Form.Control
                  type="number"
                  name="max_uses"
                  value={formData.max_uses}
                  onChange={handleChange}
                  placeholder="0 = unlimited"
                  min="0"
                />
                <Form.Text className="text-muted">
                  Set to 0 or leave empty for unlimited uses
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="active"
                  value={formData.active}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    active: Number(e.target.value),
                  }))}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {discountCode?.current_uses > 0 && (
            <Alert variant="info">
              This code has been used
              {' '}
              <strong>{discountCode.current_uses}</strong>
              {' '}
              time(s).
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default DiscountCodeInputModal;
